import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Heart, ExternalLink } from "lucide-react";
import { mediaUrl } from "@/mediaUrl";
import { usePublicConfig } from "@/usePublicConfig";
import { resolveDonationUrl } from "@/donateUrl";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Public reader for a story article. Fetches the body in `lang` and
 *  renders heading / paragraph / image blocks. Auto-pulls a "How you can
 *  help" CTA panel from the donation_link / organization_name on the
 *  parent story doc — keeps it tonal and consistent across stories.
 *
 *  Renders nothing (returns null) when the story has no article body.
 *  That way creators can opt-in per story without leaving an empty hole
 *  on the public page. */
export default function StoryBodyView({ slug, lang, story }) {
  const [blocks, setBlocks] = useState(null);
  const [loading, setLoading] = useState(true);
  const publicCfg = usePublicConfig();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBlocks(null);
    (async () => {
      try {
        const r = await axios.get(`${API}/stories/${slug}/body`, {
          params: { lang: lang || "en" },
        });
        if (cancelled) return;
        setBlocks(r.data.blocks || []);
      } catch (_e) {
        if (!cancelled) setBlocks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, lang]);

  if (loading) {
    return (
      <div className="py-10 flex justify-center" data-testid="story-body-loading">
        <Loader2 className="w-5 h-5 animate-spin text-[color:var(--muted)]" />
      </div>
    );
  }
  if (!blocks || blocks.length === 0) return null;

  const donate = resolveDonationUrl(story, publicCfg);
  const helpUrl = donate?.url;

  return (
    <article className="prose prose-invert max-w-none mt-12 md:mt-16" data-testid="story-body">
      {blocks.map((b, i) => {
        if (b.type === "heading") {
          return (
            <h2
              key={i}
              className="font-display text-2xl md:text-3xl tracking-tight mt-8 first:mt-0"
              data-testid={`story-body-heading-${i}`}
            >
              {b.text}
            </h2>
          );
        }
        if (b.type === "paragraph") {
          return (
            <p
              key={i}
              className="text-[color:var(--text)]/90 text-base md:text-lg leading-relaxed mt-4 whitespace-pre-wrap"
              data-testid={`story-body-paragraph-${i}`}
            >
              {b.text}
            </p>
          );
        }
        if (b.type === "image") {
          return (
            <figure key={i} className="my-6" data-testid={`story-body-image-${i}`}>
              <div className="rounded-xl overflow-hidden border border-white/5 bg-black">
                <img
                  src={mediaUrl(b.url)}
                  alt={b.alt || b.caption || ""}
                  loading="lazy"
                  className="w-full h-auto block"
                />
              </div>
              {b.caption && (
                <figcaption className="text-xs text-[color:var(--muted)] mt-2 leading-relaxed text-center italic">
                  {b.caption}
                </figcaption>
              )}
            </figure>
          );
        }
        return null;
      })}

      {/* Auto "How you can help" CTA — only if the story has a donation link */}
      {helpUrl && (
        <aside
          className="mt-12 panel p-6 md:p-7 border border-[color:var(--accent)]/30"
          data-testid="story-body-help-cta"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-2 inline-flex items-center gap-1.5">
            <Heart className="w-3.5 h-3.5 fill-current" /> How you can help
          </div>
          <div className="font-display text-xl md:text-2xl leading-snug">
            {story?.organization_name
              ? `Support ${story.organization_name}`
              : `Support this story`}
          </div>
          <p className="text-sm text-[color:var(--muted)] mt-2 leading-relaxed">
            Even small contributions and shares help these stories reach the people who can make a difference.
          </p>
          <a
            href={helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-[color:var(--accent)] text-black font-medium text-sm hover:opacity-90 transition-opacity"
            data-testid="story-body-help-link"
          >
            Donate now <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </aside>
      )}
    </article>
  );
}
